// GtBulkNoticeModal — "Tell 45 candidates?"
//
// After a bulk move, one box instead of one per person. It shows how many
// would be told, what they would be told, one sample email, and everyone who
// would not get it and why. Nothing is sent until a person presses Send.
// Decide later leaves every update open on Home.
import { useState, useEffect, useCallback } from 'react';

export default function GtBulkNoticeModal({ ids, authFetch, onClose }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      setP(await authFetch('/api/notices/preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }),
      }));
    } catch (e) { setError(e.message); }
  }, [authFetch, ids]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  async function decide(action) {
    setBusy(action); setError(null);
    try {
      const r = await authFetch('/api/notices/bulk', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, action }),
      });
      setDone({ ...r, action });
    } catch (e) { setError(e.message); }
    setBusy(null);
  }

  const n = p ? p.pending : ids.length;
  const seconds = p ? Math.max(1, Math.ceil(p.sendable * 0.6)) : 0;
  const canSend = p && p.sendable > 0 && p.sending_enabled && !done;

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtnt-modal" role="dialog" aria-modal="true" aria-labelledby="gtnt-bulk-title" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head" id="gtnt-bulk-title">
          {'Tell ' + n + ' candidate' + (n === 1 ? '' : 's') + '?'}
        </div>

        <div className="gtc-modal-body gtnt-body">
          {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
          {!p && !error && <div className="gtnt-loading">Checking who can be told…</div>}

          {p && !done && (
            <>
              <div className="gtnt-change">
                <span className="gtnt-role">What they would be told</span>
                <span className="gtnt-tally">
                  {Object.keys(p.by_update).map(k => (
                    <span key={k} className="gtnt-tallyitem"><b>{p.by_update[k]}</b> {k}</span>
                  ))}
                </span>
              </div>

              {!p.sending_enabled && (
                <div className="gtnt-banner gtnt-banner--amber">
                  <b>Email is switched off on this server,</b> so nothing will be delivered.
                </div>
              )}

              {p.blocked.length > 0 && (
                <div className="gtnt-banner gtnt-banner--amber">
                  <b>{p.blocked.length} will not get it.</b> If you send, they are marked as not told, so they leave Home.
                  <ul className="gtnt-blocked">
                    {p.blocked.map(x => <li key={x.id}><b>{x.name}</b> {x.reason}</li>)}
                  </ul>
                </div>
              )}

              {p.already > 0 && (
                <div className="gtnt-banner gtnt-banner--slate">{p.already} already dealt with, so they are left alone.</div>
              )}

              {p.sample && (
                <div className="gtnt-email">
                  <div className="gtnt-meta">
                    <div><span>Sample</span><b>{p.sample.name} · {p.sample.to}</b></div>
                    <div><span>Subject</span><b>{p.sample.subject}</b></div>
                  </div>
                  <iframe className="gtnt-preview" title="Sample email" sandbox="" srcDoc={
                    '<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:18px 20px;font:14px/1.6 Inter,system-ui,sans-serif;color:#161618}</style></head><body>' + p.sample.html + '</body></html>'
                  } />
                </div>
              )}

              {busy === 'send' && (
                <div className="gtnt-banner gtnt-banner--slate">Sending at a steady pace. This takes about {seconds} second{seconds === 1 ? '' : 's'}. Keep this open.</div>
              )}
            </>
          )}

          {done && (
            <div className={'gtnt-banner ' + (done.not_delivered && done.not_delivered.length ? 'gtnt-banner--amber' : 'gtnt-banner--green')}>
              {done.action === 'send' && (
                <>
                  <b>Sent to {done.sent}.</b>
                  {done.not_delivered && done.not_delivered.length > 0 && (
                    <> {done.not_delivered.length} not told:
                      <ul className="gtnt-blocked">
                        {done.not_delivered.map(x => <li key={x.id}><b>{x.name}</b> {x.reason}</li>)}
                      </ul>
                    </>
                  )}
                </>
              )}
              {done.action === 'dismiss' && <b>Not sent. Nothing went to {done.dismissed} candidate{done.dismissed === 1 ? '' : 's'}.</b>}
              {done.already > 0 && <div>{done.already} had already been dealt with.</div>}
            </div>
          )}
        </div>

        <div className="gtc-modal-foot gtnt-foot">
          <button className="gtc-btn-ghost" disabled={!!busy} onClick={onClose}>{done ? 'Close' : 'Decide later'}</button>
          <span className="gtnt-spacer" />
          {!done && p && p.pending > 0 && (
            <>
              <button className="gtc-btn-ghost" disabled={!!busy} onClick={() => decide('dismiss')}>Don't send</button>
              <button className="gtc-primary" disabled={!!busy || !canSend} onClick={() => decide('send')}>
                {busy === 'send' ? 'Sending…' : 'Send to ' + p.sendable}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
